import { writable } from 'svelte/store';

export const showBleControlSetupModal = writable(undefined);

/**
 * When `true`, the global `bleControlHandler` will *not* surface the
 * GBLE-style "Press device button for 10 seconds" pair modal triggered by
 * `pairingModeRequired` (status index 4). This is required for surfaces
 * that drive their own bonding flow on mains-powered Gen2 devices — those
 * devices enter pairing mode via cloud RPC (`Ble.StartPairing`) and the
 * GBLE button-press modal is meaningless and confusing for the user.
 */
export const suppressBleControlSetupModal = writable(false);

/**
 * When `true`, the global `bleControlHandler` will *not* react to the
 * cluster of teardown errors the native BLE bridge fires after a
 * successful one-shot operation on FW ≥2.0.0 devices — currently:
 *  - `unpairDeviceRequired` (error index 10)
 *  - `bondingFailed`        (error index 24)
 *
 * For surfaces that drive their own session lifecycle (e.g. the
 * Bluetooth RPC tester, where the native bridge routinely tears down
 * the BLE link after every BRPC frame and the bond fails on the way
 * out), the global handler's default reaction — force-disconnect plus a
 * danger toast — is just background noise the user can't act on inline.
 * Opting out keeps the form usable for back-to-back sends; the surface
 * is then responsible for its own reconnect strategy.
 */
export const suppressBleSessionTeardownAlerts = writable(false);

interface controlBleDeviceState {
	currentStatus: string;
	currentStatusTranslationKey: string;
	currentStatusIndex: number;
	currentError: string;
	currentErrorIndex: number;
	lastTimestamp: number;
	processRunning: boolean;
	searchingState: boolean;
	connectionState: number;
	deviceId: string;
	hasUpdate: boolean;
	updateVersion: string;
	updateProgress: number;
	encryptionKey: string;
}

const initControlBleDevice = () => {
	const controlBleDeviceDefaultState: controlBleDeviceState = {
		currentStatus: '',
		currentStatusTranslationKey: '',
		currentStatusIndex: null,
		currentError: '',
		currentErrorIndex: null,
		lastTimestamp: 0,
		processRunning: false,
		searchingState: false,
		connectionState: 0,
		deviceId: '',
		hasUpdate: false,
		updateVersion: '',
		updateProgress: 0,
		encryptionKey: '', //couldn't get better solution
	};

	const { subscribe, set, update } = writable(controlBleDeviceDefaultState);

	return {
		subscribe,
		updateDeviceId: (id) => {
			update(({ deviceId, ...rest }) => ({
				...rest,
				deviceId: id,
			}));
		},
		updateControlStatus: (
			status,
			status_index,
			status_translation_key,
			error,
			error_index,
			ts,
			enc_key,
		) => {
			if (status_index !== 26) {
				update(
					({
						currentStatus,
						currentStatusIndex,
						lastTimestamp,
						...rest
					}: controlBleDeviceState) => ({
						...rest,
						currentStatus: status,
						currentError: error,
						currentErrorIndex: error_index,
						currentStatusTranslationKey: status_translation_key,
						currentStatusIndex: status_index,
						lastTimestamp: ts,
						encryptionKey: enc_key,
					}),
				);
			}
		},
		updateConnectionState: (state) => {
			update(({ connectionState, ...rest }: controlBleDeviceState) => ({
				...rest,
				connectionState: state,
			}));
		},
		updateSearchingState: (state) => {
			update(({ searchingState, ...rest }: controlBleDeviceState) => ({
				...rest,
				searchingState: state,
			}));
		},
		updateHasFWUpdateState: (state, version) => {
			update(({ hasUpdate, ...rest }: controlBleDeviceState) => ({
				...rest,
				hasUpdate: state,
				updateVersion: version,
			}));
		},
		updateFWUpdateProgressState: (state) => {
			update(({ updateProgress, ...rest }: controlBleDeviceState) => ({
				...rest,
				updateProgress: state,
			}));
		},
		updateProcessRunning: (state) => {
			update(({ processRunning, ...rest }: controlBleDeviceState) => ({
				...rest,
				processRunning: state,
			}));
		},
		reset: () => {
			set(controlBleDeviceDefaultState);
		},
	};
};
export const controlBleDevice = initControlBleDevice();
